import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { httpPost, httpPostFormData } from '../services/https';
import Layout from '../components/Layout';

const AddOdf = () => {
  const [lga, setLga] = useState('');
  const [certified, setCertified] = useState(0);
  const [communities, setCommunities] = useState(0);
  const [submittingManual, setSubmittingManual] = useState(false);

  // Bulk Excel Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);

  const navigate = useNavigate();

  const handleManualSubmit = async (event) => {
    event.preventDefault();
    setSubmittingManual(true);
    try {
      const formData = {
        lga,
        no_of_communities: parseInt(communities) || 0,
        no_of_certified: parseInt(certified) || 0
      };

      await httpPost('/odf/odf', formData);
      navigate('/odf/status');
    } catch (error) {
      console.error('Error creating ODF record:', error);
      alert('Failed to create ODF record');
    } finally {
      setSubmittingManual(false);
    }
  };

  const handleExcelUpload = async (event) => {
    event.preventDefault();
    if (!uploadFile) {
      alert('Please select an Excel file (.xlsx)');
      return;
    }
    setUploading(true);
    setUploadMsg(null);

    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const res = await httpPostFormData('/odf/cds/upload', formData);
      if (res && res.status) {
        setUploadMsg({ type: 'success', text: res.message || 'CDS Excel file uploaded & imported successfully!' });
        setTimeout(() => {
          navigate('/odf/status');
        }, 1500);
      } else {
        setUploadMsg({ type: 'error', text: res?.message || 'Upload failed' });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadMsg({ type: 'error', text: 'Failed to upload Excel file' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Add & Manage ODF / CDS Data</h1>
            <p className="text-sm text-gray-500 mt-1">
              Upload updated SURWASH CDS Excel files or manually add LGA status entries.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/odf/status"
              className="inline-flex items-center px-4 py-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-medium rounded-lg transition text-xs"
            >
              📋 ODF Directory & Table
            </Link>
            <Link
              to="/odf"
              className="inline-flex items-center px-4 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-medium rounded-lg transition text-xs"
            >
              🗺️ ODF Map
            </Link>
          </div>
        </div>

        {/* Section 1: Excel Bulk Upload */}
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📊</span>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Option 1: Upload CDS Excel File (Bulk Import / Update)</h2>
              <p className="text-xs text-gray-500">
                Upload `CDS for SURWASH LGAs SOBA,IGABI,JABA,JEMAA,&SGARI.xlsx` or any updated CDS spreadsheet.
              </p>
            </div>
          </div>

          <form onSubmit={handleExcelUpload} className="space-y-4 border-t pt-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Select Excel Spreadsheet (.xlsx)</label>
              <input
                type="file"
                accept=".xlsx, .xls"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-gray-50"
              />
            </div>

            {uploadMsg && (
              <div
                className={`p-3 rounded-lg text-xs font-semibold ${
                  uploadMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                }`}
              >
                {uploadMsg.text}
              </div>
            )}

            <button
              type="submit"
              disabled={uploading || !uploadFile}
              className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-lg shadow-sm transition disabled:opacity-50"
            >
              {uploading ? 'Processing & Updating Data...' : 'Upload & Process CDS File'}
            </button>
          </form>
        </div>

        {/* Section 2: Manual Entry */}
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📝</span>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Option 2: Add Single LGA Summary Record Manually</h2>
            </div>
          </div>

          <form onSubmit={handleManualSubmit} className="space-y-4 border-t pt-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">LGA Name</label>
              <input
                type="text"
                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                value={lga}
                onChange={(e) => setLga(e.target.value)}
                placeholder="e.g. Soba"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Total Communities</label>
                <input
                  type="number"
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm"
                  value={communities}
                  onChange={(e) => setCommunities(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Certified ODF Communities</label>
                <input
                  type="number"
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm"
                  value={certified}
                  onChange={(e) => setCertified(e.target.value)}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submittingManual}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-lg shadow-sm transition"
            >
              {submittingManual ? 'Creating...' : 'Create LGA Entry'}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AddOdf;